import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

/**
 * What an "on" toggle means, not what colour it is.
 *
 * Kept as a prop because the master screens do use it to say different things — green for a record
 * that is live, amber for a setting with a consequence — and flattening that to one colour would
 * lose a real signal. What was not deliberate is the geometry: the same switch existed at two
 * widths with two throw distances across eight uses, which is why this component exists.
 */
type Tone = 'brand' | 'success' | 'warning';

const toneClasses: Record<Tone, string> = {
  brand:   'peer-checked:bg-brand-500',
  success: 'peer-checked:bg-emerald-500',
  warning: 'peer-checked:bg-amber-500',
};

interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'size'> {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  tone?: Tone;
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  ({ label, description, checked, onChange, disabled, tone = 'success', className, ...props }, ref) => (
    <label
      className={cn(
        'flex items-start gap-3 py-1.5',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <div className="relative mt-0.5 flex-shrink-0">
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
          {...props}
        />
        <div
          className={cn(
            'w-9 h-5 rounded-full bg-gray-200 dark:bg-gray-600 transition-colors',
            'peer-disabled:opacity-50',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2',
            toneClasses[tone],
          )}
        />
        <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
      </div>
      <div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        {description && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{description}</p>}
      </div>
    </label>
  ),
);

Toggle.displayName = 'Toggle';
